import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { type UploadResult } from "@/lib/upload";
import { Loader2, Upload, X } from "lucide-react";

// Carried forward verbatim (behaviour + markup) from the legacy
// pages/expenses.tsx's private FileField — shows a "View" link + remove
// once uploaded, else an upload button. Extracted to a shared component so
// every Phase 57 expense/ledger surface (bill editor, payment dialog,
// schedules) that needs an invoice/proof upload uses the SAME control.
export function FileField({
  value,
  onChange,
  uploadFn,
  label,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  uploadFn: (f: File) => Promise<UploadResult>;
  label: string;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  async function handle(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await uploadFn(f);
      if (r.ok) {
        onChange(r.url);
        toast({ title: `${label} uploaded.` });
      } else {
        toast({ title: r.message, variant: "destructive" });
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm underline truncate flex-1">
          View {label.toLowerCase()}
        </a>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <label
      className={`flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted/50 ${uploading ? "pointer-events-none opacity-60" : ""}`}
    >
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      <span>{uploading ? "Uploading…" : `Upload ${label.toLowerCase()}`}</span>
      <input
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
        className="sr-only"
        onChange={handle}
        disabled={uploading}
      />
    </label>
  );
}
